import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';

const GoogleProfile = () => {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  useEffect(() => {
    // Get user info from stored user object
    if (user?.user) {
      setUserInfo({
        name: `${user.user.first_name} ${user.user.last_name}`,
        email: user.user.email,
        picture: user.user.picture || `https://via.placeholder.com/32x32/039772/ffffff?text=${user.user.first_name?.charAt(0) || 'U'}`
      });
    }
  }, [user]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          dropdownMenuRef.current && !dropdownMenuRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    const handleResize = () => {
      if (showDropdown) {
        calculateDropdownPosition();
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [showDropdown]);

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      console.log("Google login successful, credential received:", credentialResponse);
      
      // Send JWT to Django backend
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
      const response = await fetch(`${API_URL}/api/auth/google/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: credentialResponse.credential })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Backend response:", data);
      
      // Store tokens
      localStorage.setItem("access_token", data.access);
      localStorage.setItem("refresh_token", data.refresh);
      
      // Update auth context
      login({
        accessToken: data.access,
        refreshToken: data.refresh,
        user: data.user
      });

      // Set user info from backend response
      setUserInfo({
        name: `${data.user.first_name} ${data.user.last_name}`,
        email: data.user.email,
        picture: data.user.picture || `https://via.placeholder.com/32x32/039772/ffffff?text=${data.user.first_name?.charAt(0) || 'U'}`
      });

    } catch (error) {
      console.error("Google login error:", error);
      alert(`Google login failed: ${error.message}`);
    }
  };

  const handleGoogleError = (error) => {
    console.error("Google login failed:", error);
    alert("Google login was unsuccessful. Please try again later.");
  };

  const handleLogout = () => {
    logout();
    setUserInfo(null);
    setShowDropdown(false);
  };

  const calculateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 5, // No window.scrollY since navbar is fixed
        right: window.innerWidth - rect.right // No window.scrollX since navbar is fixed
      });
    }
  };

  const handleProfileClick = () => {
    if (!showDropdown) {
      calculateDropdownPosition();
    }
    setShowDropdown(!showDropdown);
  };

  if (!user) {
    return (
      <div className="google-login-container">
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          useOneTap={false}
          auto_select={false}
          theme="outline"
          size="large"
          text="signin_with"
          shape="rectangular"
          width="120"
        />
      </div>
    );
  }

  return (
    <div className="profile-dropdown" ref={dropdownRef}>
      <button 
        ref={buttonRef}
        className="profile-button"
        onClick={handleProfileClick}
      >
        <img 
          src={userInfo?.picture || 'https://via.placeholder.com/32x32/039772/ffffff?text=U'} 
          alt="Profile" 
          className="profile-picture"
        />
        <span>{userInfo?.name || 'User'}</span>
        <span>▼</span>
      </button>
      
      {showDropdown && createPortal(
        <div 
          ref={dropdownMenuRef}
          className="dropdown-menu-portal"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            right: dropdownPosition.right,
            zIndex: 99999
          }}
        >
          <div className="dropdown-menu">
            <div className="dropdown-item dropdown-user-info">
              <strong>{userInfo?.name || 'User'}</strong>
              <br />
              <small>{userInfo?.email || 'user@example.com'}</small>
            </div>
            <button 
              className="dropdown-item dropdown-item-button" 
              onClick={() => {
                navigate('/profile');
                setShowDropdown(false);
              }}
            >
              Profile
            </button>
            <button 
              className="dropdown-item dropdown-item-button" 
              onClick={() => {
                navigate('/profile/settings');
                setShowDropdown(false);
              }}
            >
              Settings
            </button>
            <button className="logout-button" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default GoogleProfile;
